/**
 * DzAnalysisSetupModal — the downsizing Suggestion Engine's "Strategy Analysis Setup" (owner,
 * 2 Oct 2026: "we need some kind of suggestion engine strategy analyser set-up that puts in place
 * certain factors that the client wants us to consider — e.g. selling, renting and investing the
 * rest"; and "it doesn't ask the likely purchase price of the new property").
 *
 * The engine builds strategies around the choices only the client can make, so each is asked here,
 * once, before the run — and only where a strategy would turn on it:
 *   - buy or rent after the sale, or compare both; the new home's price and state (its duty), or
 *     the weekly rent;
 *   - whether a lifetime annuity may be offered, in everyday words, and the most of the freed-up
 *     money it may take (a quarter by default);
 *   - how new money would be invested, and the pension cap — the two just-in-time questions every
 *     engine asks, folded in so the adviser answers once, on one screen.
 *
 * Mirrors RvAnalysisSetupModal in shape and voice. Cancel runs on the defaults; Continue saves the
 * answers to the client record and the engine runs.
 */
import React, { useEffect, useState } from 'react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import { INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS, type InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import { STAMP_DUTY_STATES, isStampDutyState, calculateStampDuty } from '../../../../shared/src/calculation/stampDuty.ts';
import { GENERAL_TRANSFER_BALANCE_CAP } from '../../constants/index.ts';
import { guaranteeWindowWords } from '../../components/strategy/annuityWords.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import type { AnnuityAppetite, AnnuityResidual } from '../../../../shared/src/calculation/annuityPreference.ts';
import { DEFAULT_DZ_ANNUITY_SHARE_CAP_PCT, DZ_SETUP_SPEND_THRESHOLD, type DzEngineSetup, type DzHousing, type DzMotive } from './downsizerScenario.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

export interface DzAnalysisSetupExtras {
    /** The new-money profile chosen here, or null when skipped (run at Conservative, ask again next time). */
    newMoneyProfile: InvestmentProfileId | null;
    /** The transfer balance account figure, or null when skipped (estimate from current balances). */
    tbcAmount: number | null;
}

export interface DzAnalysisSetupModalProps {
    isOpen: boolean;
    onClose: () => void;
    factFind: DraftFactFind;
    /** The answers as they stand (saved on the client record, or Manual Control's figures). */
    initial: DzEngineSetup;
    salePrice: number;
    sellingCosts: number;
    /** Ask how new money would be invested (only when unanswered). */
    askNewMoney: boolean;
    /** Ask the transfer balance cap question for this person (only when the $1.6m screen trips). */
    tbcPerson: 'client' | 'partner' | null;
    onContinue: (setup: DzEngineSetup, extras: DzAnalysisSetupExtras) => void;
}

const clampPct = (v: unknown): number => {
    const n = Number(v);
    if (!Number.isFinite(n)) return DEFAULT_DZ_ANNUITY_SHARE_CAP_PCT;
    return Math.min(100, Math.max(0, Math.round(n)));
};

/*
 * A GRID, NOT A WRAPPING ROW (owner, 27 Sep 2026): equal columns make the answers read as
 * alternatives of equal standing. The column count has to be a literal class name.
 */
const CHOICE_COLUMNS: Record<number, string> = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' };

function Choice<T extends string | number>({ options, value, onChange, ariaLabel }: { options: Array<{ id: T; label: string }>; value: T; onChange: (v: T) => void; ariaLabel: string }) {
    return (
        <div className={`grid grid-cols-1 ${CHOICE_COLUMNS[options.length] || 'sm:grid-cols-3'} gap-2`} role="radiogroup" aria-label={ariaLabel}>
            {options.map((o) => (
                <button
                    key={String(o.id)}
                    type="button"
                    role="radio"
                    aria-checked={value === o.id}
                    onClick={() => onChange(o.id)}
                    className={`rounded-lg border-2 px-3.5 py-2.5 text-sm font-semibold transition-all ${
                        value === o.id ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-sm' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

const SectionIcon = ({ path }: { path: string }) => (
    <div className="p-2 bg-blue-100 rounded-lg shrink-0">
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
        </svg>
    </div>
);

const Section = ({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) => (
    <div className="bg-white border border-blue-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
            <SectionIcon path={icon} />
            <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-blue-800 mb-1">{title}</h3>
                {children}
            </div>
        </div>
    </div>
);

const ICON_HOME = 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6';
const ICON_COINS = 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
const ICON_TREND = 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6';
const ICON_LOCK = 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z';

const DzAnalysisSetupModal = ({ isOpen, onClose, factFind, initial, salePrice, sellingCosts, askNewMoney, tbcPerson, onContinue }: DzAnalysisSetupModalProps) => {
    const [motive, setMotive] = useState<DzMotive>(initial.motive ?? 'both');
    const [housing, setHousing] = useState<DzHousing>(initial.housing ?? 'buy');
    const [newHomePrice, setNewHomePrice] = useState<number | ''>(Number(initial.newHomePrice) > 0 ? Number(initial.newHomePrice) : '');
    const [newHomeState, setNewHomeState] = useState<string>(initial.newHomeState || '');
    const [rentPerWeek, setRentPerWeek] = useState<number | ''>(Number(initial.rentPerWeek) > 0 ? Number(initial.rentPerWeek) : '');
    const [annuityCapPct, setAnnuityCapPct] = useState<number>(clampPct(initial.annuityShareCapPct ?? DEFAULT_DZ_ANNUITY_SHARE_CAP_PCT));
    const [annuityAppetite, setAnnuityAppetite] = useState<AnnuityAppetite>(initial.annuityAppetite ?? 'unsure');
    const [annuityResidual, setAnnuityResidual] = useState<AnnuityResidual>(initial.annuityResidual ?? 'both');
    const [newMoneyProfile, setNewMoneyProfile] = useState<InvestmentProfileId | ''>('');
    const [tbcAmount, setTbcAmount] = useState<number | ''>('');

    useEffect(() => {
        if (!isOpen) return;
        setMotive(initial.motive ?? 'both');
        setHousing(initial.housing ?? 'buy');
        setNewHomePrice(Number(initial.newHomePrice) > 0 ? Number(initial.newHomePrice) : '');
        setNewHomeState(initial.newHomeState || '');
        setRentPerWeek(Number(initial.rentPerWeek) > 0 ? Number(initial.rentPerWeek) : '');
        setAnnuityCapPct(clampPct(initial.annuityShareCapPct ?? DEFAULT_DZ_ANNUITY_SHARE_CAP_PCT));
        setAnnuityAppetite(initial.annuityAppetite ?? 'unsure');
        setAnnuityResidual(initial.annuityResidual ?? 'both');
        setNewMoneyProfile('');
        setTbcAmount('');
    }, [isOpen, initial.motive, initial.housing, initial.newHomePrice, initial.newHomeState, initial.rentPerWeek, initial.annuityShareCapPct, initial.annuityAppetite, initial.annuityResidual]);

    if (!isOpen) return null;

    const netProceeds = Math.max(0, salePrice - sellingCosts);
    const buys = housing === 'buy' || housing === 'both';
    const rents = housing === 'rent' || housing === 'both';
    const chosenState = isStampDutyState(newHomeState) ? STAMP_DUTY_STATES.find((s) => s.code === newHomeState) : undefined;
    const anchor = Number(newHomePrice) > 0 ? Number(newHomePrice) : Math.round(salePrice * 0.7);
    const dutyOnAnchor = chosenState ? (calculateStampDuty(chosenState.code, anchor) ?? 0) : 0;
    const freedBuying = Math.max(0, netProceeds - anchor - dutyOnAnchor);
    const freed = rents ? netProceeds : freedBuying;
    const spends = freed >= DZ_SETUP_SPEND_THRESHOLD;
    const rentMissing = rents && !(Number(rentPerWeek) > 0);
    const offerGuaranteed = guaranteedShapeAvailable(factFind as never);
    const guaranteeWindow = guaranteeWindowWords({
        age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
        sex: factFind?.client?.sex,
        partnerAge: factFind?.adviceFor === 'couple' && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null,
        partnerSex: factFind?.partner?.sex,
        ownerName: factFind?.client?.firstName,
        partnerName: factFind?.partner?.firstName,
    });
    const annuityDollars = Math.round(freed * (annuityCapPct / 100));
    const tbcName = (tbcPerson === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person';

    const handleContinue = () => {
        onContinue(
            {
                motive,
                housing,
                newHomePrice: Number(newHomePrice) > 0 ? Number(newHomePrice) : 0,
                newHomeState: chosenState ? chosenState.code : '',
                rentPerWeek: Number(rentPerWeek) > 0 ? Number(rentPerWeek) : 0,
                annuityShareCapPct: annuityCapPct,
                annuityAppetite,
                annuityResidual,
                cashflowTarget: 0,
            },
            { newMoneyProfile: newMoneyProfile === '' ? null : newMoneyProfile, tbcAmount: tbcAmount === '' ? null : Number(tbcAmount) },
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="dz-setup-title">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-teal-400 text-white p-6 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black opacity-10"></div>
                    <div className="relative z-10 flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h2 id="dz-setup-title" className="text-2xl font-bold mb-1">Strategy Analysis Setup</h2>
                                <p className="text-blue-100">A few choices the strategies turn on — yours to make, not the tool&apos;s</p>
                            </div>
                        </div>
                        <button type="button" onClick={onClose} aria-label="Close" className="text-white hover:text-blue-200 text-2xl leading-none transition-colors p-1 hover:bg-white hover:bg-opacity-10 rounded-full">
                            &times;
                        </button>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(85vh-180px)]">
                    <div className="space-y-5">
                        {/* 1. Why they are moving — it decides whether a dearer home is a strategy or a what-if. */}
                        <Section icon={ICON_HOME} title="Why you are moving">
                            <p className="text-sm text-slate-700 mb-3">
                                The reason changes which answers are right. Buying cheaper frees money that Centrelink then counts; buying at or above what you sell for moves savings into a home that is not counted, which can <strong>lift</strong> the Age Pension. <strong>Why are you thinking of moving?</strong>
                            </p>
                            <Choice<DzMotive>
                                ariaLabel="Why are you moving"
                                value={motive}
                                onChange={setMotive}
                                options={[
                                    { id: 'money', label: 'To free up money' },
                                    { id: 'home', label: 'For a home that suits us better' },
                                    { id: 'both', label: 'Both — compare everything' },
                                ]}
                            />
                            <p className="text-xs text-gray-500 mt-2">
                                {motive === 'money'
                                    ? 'The strategies work with the price you name. A cheaper, a similar and a dearer move are shown as what-ifs, never recommended.'
                                    : motive === 'home'
                                        ? 'A home at a similar price, a somewhat dearer one, and a dearer home bought with your savings all compete for the goals alongside the price you name — with the bills checked. A cheaper home is shown as a what-if.'
                                        : 'Every price competes for the goals, from a cheaper home to a dearer one bought with your savings.'}
                            </p>
                        </Section>

                        {/* 2. Buy or rent — the first decision, and the one the engine used to make for the client. */}
                        <Section icon={ICON_HOME} title="After the sale">
                            <p className="text-sm text-slate-700 mb-3">
                                The sale nets about <strong>{formatCurrency(netProceeds)}</strong> after selling costs. <strong>Will you buy another home, or rent?</strong>{' '}
                                Buying keeps a home out of the Age Pension test; renting frees the most money but every dollar is then counted, and the rent is a cost for life.
                            </p>
                            <Choice<DzHousing>
                                ariaLabel="Buy or rent after the sale"
                                value={housing}
                                onChange={setHousing}
                                options={[
                                    { id: 'buy', label: 'Buy another home' },
                                    { id: 'rent', label: 'Rent instead' },
                                    { id: 'both', label: 'Compare both' },
                                ]}
                            />
                            {buys && (
                                /* Two rows, not two columns: the inputs on the first, their helper
                                   lines on the second, so the two helpers always start level
                                   (owner-caught, 3 Oct 2026). */
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
                                    <div>
                                        <CurrencyInput
                                            id="dz-setup-price"
                                            label="What will the new home cost?"
                                            value={newHomePrice}
                                            onChange={(v) => setNewHomePrice(v ?? '')}
                                            placeholder={`Not sure — assume ${formatCurrency(Math.round(salePrice * 0.7))}`}
                                            className=""
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="dz-setup-state">Which state is it in?</label>
                                        <select
                                            id="dz-setup-state"
                                            value={chosenState ? chosenState.code : ''}
                                            onChange={(e) => setNewHomeState(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Not sure — leave stamp duty out</option>
                                            {STAMP_DUTY_STATES.map((s) => (
                                                <option key={s.code} value={s.code}>{s.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        Leave it blank and the strategies are built around {formatCurrency(Math.round(salePrice * 0.7))}.{' '}
                                        {motive === 'money' ? 'A cheaper, a similar and a dearer move are shown beside them as what-ifs.' : 'A similar and a dearer home are modelled beside it as strategies in their own right.'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {chosenState
                                            ? `Stamp duty of about ${formatCurrency(dutyOnAnchor)} on the new home, on ${chosenState.name}'s standard scale with no concessions. The same state prices any investment property a strategy buys.`
                                            : 'With no state chosen no duty is charged on the new home or on a bought investment property, and every card says so. No state, no guess.'}
                                    </p>
                                </div>
                            )}
                            {rents && (
                                <div className="mt-4 max-w-xs">
                                    <CurrencyInput id="dz-setup-rent" label="Rent you would pay (a week)" value={rentPerWeek} onChange={(v) => setRentPerWeek(v ?? '')} className="" />
                                    <p className={`text-xs mt-1 ${rentMissing ? 'text-amber-700' : 'text-gray-500'}`}>
                                        {rentMissing
                                            ? 'Needed for an honest comparison: the rent is charged every year, rises with inflation, and is what Rent Assistance is assessed on.'
                                            : `About ${formatCurrency(Math.round(Number(rentPerWeek) * 52))} a year, rising with inflation. Rent Assistance is assessed on it and paid with the pension.`}
                                    </p>
                                </div>
                            )}
                        </Section>

                        {/* 2. Guaranteed income for life — only where the sale frees enough to lock part away. */}
                        {spends && (
                            <Section icon={ICON_COINS} title="Guaranteed income for life">
                                <p className="text-sm text-slate-700 mb-3">
                                    Some strategies lock part of the freed-up money away with an insurer in exchange for a <strong>guaranteed income for life</strong> (a lifetime annuity).
                                    Centrelink counts only 60% of that money, which can lift the Age Pension. Setting one up takes more than a form:{' '}
                                    you get a quote from the insurer, directly or through an adviser, then apply.{' '}
                                    <strong>Should we include these options?</strong>
                                </p>
                                <Choice<AnnuityAppetite>
                                    ariaLabel="Should we include a lifetime annuity"
                                    value={annuityAppetite}
                                    onChange={setAnnuityAppetite}
                                    options={[
                                        { id: 'none', label: 'No — keep it all accessible' },
                                        { id: 'yes', label: 'Yes — include these options' },
                                    ]}
                                />
                                {annuityAppetite === 'yes' && (
                                    <>
                                        <p className="mt-4 mb-2 text-sm text-slate-700">
                                            <strong>Should any of it come back to the family on death?</strong> Guaranteeing more means a lower income.
                                            The full amount only comes back if death comes early{guaranteeWindow ? `, and ${guaranteeWindow}` : ''}; after that it falls away to nothing.
                                        </p>
                                        <Choice<AnnuityResidual>
                                            ariaLabel="Should the money come back to the family"
                                            value={annuityResidual}
                                            onChange={setAnnuityResidual}
                                            options={[
                                                { id: 'returned', label: 'Yes — as much as the rules allow' },
                                                ...(offerGuaranteed ? [{ id: 'guaranteed' as const, label: 'Yes — the full amount, whenever I die' }] : []),
                                                { id: 'nil', label: 'No — the highest income' },
                                                { id: 'both', label: 'Show me whichever works best' },
                                            ]}
                                        />
                                    </>
                                )}
                                {annuityAppetite !== 'none' && (
                                    <>
                                        <p className="mt-4 mb-2 text-sm text-slate-700"><strong>How much of the {formatCurrency(freed)} freed up could be locked away, at most?</strong></p>
                                        <Choice<number>
                                            ariaLabel="How much could be locked away at most"
                                            value={annuityCapPct}
                                            onChange={setAnnuityCapPct}
                                            options={[
                                                { id: 10, label: 'A small part (10%)' },
                                                { id: 25, label: 'A quarter (25%)' },
                                                { id: 50, label: 'Half (50%)' },
                                                { id: 100, label: 'All of it (100%)' },
                                                ...([10, 25, 50, 100].includes(annuityCapPct) ? [] : [{ id: annuityCapPct, label: `${annuityCapPct}% (as set)` }]),
                                            ]}
                                        />
                                        <p className="text-xs text-gray-500 mt-2">
                                            = <span className="font-semibold tabular-nums">{formatCurrency(annuityDollars)}</span>. The rest stays invested and accessible.{' '}
                                            {annuityDollars < 25_000
                                                ? 'Under $25,000 is too small to be worth modelling, so no such strategy will be offered at this setting.'
                                                : annuityAppetite === 'unsure' || annuityResidual === 'both'
                                                    ? 'Both versions are modelled: as much coming back as the rules allow (a lower income), and nothing coming back (a higher income).'
                                                    : annuityResidual === 'returned'
                                                        ? 'Only the version where the money comes back is modelled.'
                                                        : 'Only the higher-income version, where nothing comes back, is modelled.'}
                                        </p>
                                    </>
                                )}
                                {annuityAppetite === 'none' && (
                                    <p className="text-xs text-gray-500 mt-2">No strategy that locks money away for life will be offered.</p>
                                )}
                            </Section>
                        )}

                        {/* 3. How new money is invested — the question every engine asks once */}
                        {askNewMoney && (
                            <Section icon={ICON_TREND} title="Investing freed-up money">
                                <p className="text-sm text-slate-700 mb-3">
                                    Some strategies invest what the sale frees. <strong>How would that money be invested?</strong> Leave it and the tool assumes Conservative, the most cautious option, and asks again next time.
                                </p>
                                <select
                                    aria-label="How new money would be invested"
                                    value={newMoneyProfile}
                                    onChange={(e) => setNewMoneyProfile(e.target.value as InvestmentProfileId | '')}
                                    className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Not sure — assume Conservative (most cautious)</option>
                                    {INVESTMENT_PROFILE_IDS.map((id) => (
                                        <option key={id} value={id}>{INVESTMENT_PROFILES[id].label}</option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-2">Saved to the client profile, so you&apos;ll only be asked once.</p>
                            </Section>
                        )}

                        {/* 4. The transfer balance cap — only when the $1.6m screen trips */}
                        {tbcPerson && (
                            <Section icon={ICON_LOCK} title={`${tbcName}'s pension cap`}>
                                <p className="text-sm text-slate-700 mb-3">
                                    The government limits how much any one person may move into a retirement pension over their lifetime — currently{' '}
                                    <strong>{formatCurrency(GENERAL_TRANSFER_BALANCE_CAP)}</strong> — and {tbcName}&apos;s balances are large enough that it may now cap how much more can go in.
                                    Roughly how much has {tbcName} moved into retirement-phase pensions so far? The ATO shows this in myGov as the <strong>transfer balance account</strong>.
                                    Leave it blank and the tool estimates from current pension balances, and the advice document says so.
                                </p>
                                <div className="max-w-xs">
                                    <CurrencyInput label="Lifetime amount moved into pensions ($)" name="dzTbcAmount" value={tbcAmount} onChange={(v) => setTbcAmount(v ?? '')} />
                                </div>
                            </Section>
                        )}
                    </div>
                </div>

                <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
                    <button type="button" onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200 font-semibold">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleContinue}
                        disabled={rentMissing}
                        title={rentMissing ? 'Enter the weekly rent to continue' : undefined}
                        className="px-6 py-2 bg-gradient-to-r from-blue-500 to-teal-400 text-white rounded-lg hover:from-blue-600 hover:to-teal-500 transition-all duration-200 font-semibold shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Continue to Analysis
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DzAnalysisSetupModal;
